import type { ReactNode } from "react";
import { memo, useCallback, useEffect, useState } from "react";
import { Badge, Collapse, Grid, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import TuneIcon from "@mui/icons-material/Tune";
import Statut from "constants/Statut";
import { dateUtil } from "@sdeapps/react-core";
import type Article from "models/Article";
import ArticleInput from "components/atoms/inputs/ArticleInput";
import StatutInput from "components/atoms/inputs/StatutInput";
import TaillesInput from "components/atoms/inputs/TaillesInput";
import MagasinDeRetraitInput from "components/atoms/inputs/MagasinDeRetraitInput";
import type CommandeArticleTri from "models/CommandeArticleTri";
import NumeroBonDeCommandeInput from "components/atoms/inputs/NumeroBonDeCommandeInput";
import commandeArticleUtil from "utils/commandeArticleUtil";

type ArticleFiltersProps = {
  betterArticles: Array<CommandeArticleTri>;
  setBetterArticles: (c: Array<CommandeArticleTri>) => void;

  articleFilter?: boolean;
  tailleFilter?: boolean;
  statutFilter?: boolean;
  centreRetraitFilter?: boolean;
  numeroBonDeCommandeFilter?: boolean;

  availableArticles?: Array<Article>;
  availableStatuts?: Array<Statut>;

  selectedArticleDefault?: Article;
  selectedTaillesDefault?: Array<string>;
  selectedStatutsDefault?: Array<Statut>;
  selectedCentresRetraitDefault?: Array<string>;
};

const ArticleFilters = memo(function ArticleFilters({
  betterArticles,
  setBetterArticles,

  articleFilter = false,
  tailleFilter = false,
  statutFilter = false,
  centreRetraitFilter = false,
  numeroBonDeCommandeFilter = false,

  availableArticles = [],
  availableStatuts = Object.values(Statut),

  selectedArticleDefault,
  selectedTaillesDefault = [],
  selectedStatutsDefault = [],
  selectedCentresRetraitDefault = [],
}: Readonly<ArticleFiltersProps>): ReactNode {
  const [open, setOpen] = useState(true);

  const [selectedArticle, setSelectedArticle] = useState<Article | null>(
    selectedArticleDefault ?? null
  );
  const [selectedTailles, setSelectedTailles] = useState<Array<string>>(selectedTaillesDefault);
  const [selectedStatuts, setSelectedStatuts] = useState<Array<Statut>>(selectedStatutsDefault);
  const [selectedCentresRetrait, setSelectedCentresRetrait] = useState<Array<string>>(
    selectedCentresRetraitDefault
  );
  const [uniqueNBonDeCommande, setUniqueNBonDeCommande] = useState<Array<string>>([]);
  const [numeroBonDeCommandeSearchString, setNumeroBonDeCommandeSearchString] = useState<string>();
  const [nbrFilter, setNbrFilter] = useState<number>();

  const filterArticles = useCallback(() => {
    const filtreNumeroBonDeCommande = numeroBonDeCommandeSearchString?.trim() ?? "";

    if (selectedArticle == null && filtreNumeroBonDeCommande === "") {
      return [];
    }

    let filteredArticles: Array<CommandeArticleTri> = betterArticles;

    if (selectedArticle != null) {
      filteredArticles = filteredArticles.filter(
        (article) => article.articleId === selectedArticle.id
      );
    }

    if (selectedStatuts.length > 0) {
      filteredArticles = filteredArticles.filter((article) =>
        selectedStatuts.map((c) => c.toLowerCase()).includes(article.statutArticle.toLowerCase())
      );
    }

    if (selectedTailles.length > 0) {
      filteredArticles = filteredArticles.filter((article) =>
        selectedTailles.map((c) => c.toLowerCase()).includes(article.taille.toLowerCase())
      );
    }

    if (selectedCentresRetrait.length > 0) {
      filteredArticles = filteredArticles.filter((article) =>
        selectedCentresRetrait
          .map((c) => c.toLowerCase())
          .includes(article.magasinDeRetrait.toLowerCase())
      );
    }

    if (numeroBonDeCommandeSearchString != null && numeroBonDeCommandeSearchString.trim() !== "") {
      const numeroRegex = new RegExp(`^${numeroBonDeCommandeSearchString.trim()}$`, "i");
      filteredArticles = filteredArticles.filter((article: CommandeArticleTri) =>
        numeroRegex.test(article.numeroBonDeCommande ?? "")
      );
    }

    return filteredArticles.toSorted((a, b) => dateUtil.compareAsc(a.dateCommande, b.dateCommande));
  }, [
    selectedArticle,
    betterArticles,
    selectedStatuts,
    selectedTailles,
    selectedCentresRetrait,
    numeroBonDeCommandeSearchString,
  ]);

  useEffect(() => {
    setSelectedTailles([]);
  }, [selectedArticle]);

  useEffect(() => {
    setBetterArticles(filterArticles());

    const nbArticles =
      selectedArticle == null ? 0 : 1 + selectedStatuts.length + selectedTailles.length;
    const nbBonDeCommandes = (numeroBonDeCommandeSearchString ?? "").trim() === "" ? 0 : 1;
    setNbrFilter(nbArticles + nbBonDeCommandes);
    // TODO: revoir les dépendances lors d'une mise à jour des règles ESLint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedArticle,
    selectedStatuts,
    selectedTailles,
    selectedCentresRetrait,
    numeroBonDeCommandeSearchString,
    betterArticles,
  ]);

  useEffect(() => {
    setUniqueNBonDeCommande(commandeArticleUtil.getUniquesNumerosBonDeCommande(betterArticles));
  }, [betterArticles]);

  return (
    <Grid container spacing={2} size={12}>
      <Grid container spacing={2} size={12} sx={{ alignItems: "center" }}>
        <Badge color="error" badgeContent={nbrFilter}>
          <Button
            variant="contained"
            startIcon={<TuneIcon />}
            onClick={() => {
              setOpen(!open);
            }}>
            Filtres
          </Button>
        </Badge>
        {open && (
          <Typography variant="body2" color="textSecondary">
            Un nom d'article ou un numéro Bon de commande est requis pour afficher des résultats.
          </Typography>
        )}
      </Grid>
      <Grid container spacing={2} sx={{ margin: 0 }} size={12}>
        <Collapse in={open} timeout="auto" unmountOnExit sx={{ width: "100%" }}>
          <Grid container spacing={2} columns={{ sm: 12, xl: 10 }}>
            {articleFilter && (
              <Grid size={{ xs: 12, md: 12, lg: 6, xl: 2 }}>
                <ArticleInput
                  unique
                  availableArticles={availableArticles}
                  selectedArticle={selectedArticle}
                  setSelectedArticle={setSelectedArticle}
                />
              </Grid>
            )}
            {statutFilter && (
              <Grid size={{ xs: 12, md: 6, lg: 6, xl: 2 }}>
                <StatutInput
                  selectedStatuts={selectedStatuts}
                  setSelectedStatuts={setSelectedStatuts}
                  availableStatuts={availableStatuts}
                  label="État(s) de l'article"
                />
              </Grid>
            )}
            {tailleFilter && (
              <Grid size={{ xs: 12, md: 6, lg: 4, xl: 2 }}>
                <TaillesInput
                  selectedTailles={selectedTailles}
                  setSelectedTailles={setSelectedTailles}
                  availableTailles={selectedArticle?.tailles ?? []}
                  disabled={
                    selectedArticle?.tailles == null || selectedArticle?.tailles.length <= 0
                  }
                />
              </Grid>
            )}
            {centreRetraitFilter && (
              <Grid size={{ xs: 12, md: 6, lg: 4, xl: 2 }}>
                <MagasinDeRetraitInput
                  multiple
                  label="Magasin(s) de retrait"
                  selectedCentresRetrait={selectedCentresRetrait}
                  setSelectedCentresRetrait={setSelectedCentresRetrait}
                />
              </Grid>
            )}
            {numeroBonDeCommandeFilter && (
              <Grid size={{ xs: 12, md: 6, lg: 4, xl: 2 }}>
                <NumeroBonDeCommandeInput
                  numeroBonDeCommande={numeroBonDeCommandeSearchString}
                  setNumeroBonDeCommande={setNumeroBonDeCommandeSearchString}
                  availableNumerosBonDeCommande={uniqueNBonDeCommande}
                />
              </Grid>
            )}
          </Grid>
        </Collapse>
      </Grid>
    </Grid>
  );
});

export default ArticleFilters;
